import Vue from 'vue';
import { Helper } from '@/services/helper/helper';
import { AdServiceHelper } from '@/services/helper/adServiceHelper';
import Teaser, { CAPTION_COLORS, CAPTION_POSITION, LABEL_COLORS } from '@/components/Teaser/Teaser';

export const AD_BLOCK_TYPES = {
	none: 0,
	privateMode: 1,
	adBlocker: 2
};

/**
 * @abstract
 * Class providing functions for communication with AdService 2.
 * Is used static. Do not instantiate!
 */
class AdServiceInterface {
	/**
	 * Initializes all the data.
	 * @static
	 * @param dataName {string} - The value for data attributes [data-[dataName]-slot].
	 * @param additionalParams {object} - Additional Parameters for AdService 2 initialization.
	 * @param colCount {number} - Current column state of homepage. Needed for statistics on AdServer.
	 */
	static init (dataName, additionalParams, colCount) {
		this.serviceResponse = window.initialData.serviceResponse;

		if (dataName && typeof dataName === 'string' && dataName.length > 0) {
			this.setDataName(dataName);
		}

		const connectorUrl = AdServiceHelper.getScriptBaseUrl();

		const basicConfig = {
			'nonfriendlyiframe-document': connectorUrl + 'nonfriendlyiframe.html',
			'adservice-iframe-script-url': connectorUrl + 'connector.min.js',
			'request-ad-only-if-visible': false,
			'secure': true,
			'request-async': true
		};
		this.setConfig(basicConfig);
		this.initParams(additionalParams, colCount);

		AdServiceHelper.listenEvent('initialize.end', () => {
			window.AdService.Module('uim-cookie-exchanger').run();
			this.incrementAc();
			if (!window.AdService._render) {
				window.AdService._render = window.AdService.render;
				window.AdService.render = this.newRender;
			}
		});
	}

	/**
	 * Sets the data-[dataName]-slot for Ad Containers.
	 * @static
	 * @param dataName {string} - The attribute name.
	 */
	static setDataName (dataName) {
		this.setConfig({
			'data-attribute-namespace': dataName
		});
	}

	/**
	 * Sets configuration for AdService 2.
	 * @static
	 * @param confObject {object} - The object with the configuration fields and values.
	 */
	static setConfig (confObject) {
		AdServiceHelper.listenEvent('initialize.end', () => {
			window.AdService.setConfig(confObject);
		});
	}

	/**
	 * Listens to AdService 2 event which executes the 3rd -> 1st party cookie exchange.
	 * Then reads the 1st party cookie and calls the callback function with nguserid as parameter.
	 * @static
	 * @param func {callback} - The callback function to call which gets the nguserid.
	 */
	static getNgUserIdCookie (func) {
		AdServiceHelper.listenEvent('initialize.end', () => {
			if (typeof window.UI_nguserid === 'undefined') {
				AdServiceHelper.listenEvent('module.uim-cookie-exchanger.cookie-set', () => {
					const nguserid = Helper.getCookieValue('NGUserID');
					func(nguserid);
				});
			} else {
				const nguserid = Helper.getCookieValue('NGUserID');
				func(nguserid);
			}
		});
	}

	/**
	 * Sets the CSS for the iframe slots.
	 * @static
	 * @param slot {string} - The slot name to set the CSS for.
	 */
	static setIframeCss (slot) {
		const top = {
			rules: [
				{
					action: 'useIframeCss',
					css: 'top'
				}
			]
		};

		const middle = {
			rules: [
				{
					action: 'useIframeCss',
					css: 'middle'
				}
			]
		};

		const left = {
			rules: [
				{
					action: 'useIframeCss',
					css: 'left'
				}
			]
		};

		const right = {
			rules: [
				{
					action: 'useIframeCss',
					css: 'right'
				}
			]
		};

		window.AdService.setIframeCss({
			top: {
				css: 'html, body, body > div { width: 100%; height: 100% } iframe, a { float: right }'
			},
			middle: {
				css: 'html, body, body > div { width: 100%; height: 100% } iframe, body > div > a { display: inline-block; position: relative; left: 50%; transform: translateX(-50%) }'
			},
			left: {
				css: 'html, body, body > div { width: 100%; height: 100% } iframe, a { float: right }'
			},
			right: {
				css: 'html, body, body > div { width: 100%; height: 100% } iframe, a { float: left }'
			}
		});

		window.AdService.on('ad.request', slot, () => {
			let exclusionRules = {};

			if (slot === 'top') {
				exclusionRules.superbanner = top;
			} else if (slot === 'middle') {
				exclusionRules.billboard = middle;
			} else if (slot === 'left') {
				exclusionRules.skyRight = left;
				exclusionRules.sitebar = left;
				exclusionRules.halfpage = left;
			} else if (slot === 'right') {
				exclusionRules.skyRight = right;
				exclusionRules.sitebar = right;
				exclusionRules.halfpage = right;
			}

			window.AdService.setExclusionRuleSet(exclusionRules);
		});
	}

	/**
	 * Initializes parameters needed by AdService2 and AdServer to display correct ads.
	 * @static
	 * @param additionalParams {object} - Can be given if there are additional parameters under certain circumstances.
	 * @param colCount {number} - Current column count.
	 */
	static initParams (additionalParams, colCount) {
		const params = {};
		params.wi = this.serviceResponse._params.wi;
		params.section = this.serviceResponse._params.section;
		params.portal = this.serviceResponse._params.portal;
		params.category = this.serviceResponse._params.category;
		params.layoutclass = this.serviceResponse._params.layoutclass;
		params.nguserid = this.serviceResponse._params['nguserid'];
		params.categorytype = this.serviceResponse.response.type + '_' + colCount;
		params.consentlevel = this.serviceResponse._params['prf[consentLevel]'];
		params.weather_condition = this.serviceResponse._params['prf[weather_condition]'];
		params.weather_temp = this.serviceResponse._params['prf[weather_temp]'];
		params.pagev = 2;
		if (this.serviceResponse._params['prf[busterid]']) {
			params.hpeventid = this.serviceResponse._params['prf[busterid]'];
		}

		if (window.PageConfig.sitespect) {
			params['prf[special]'] = +window.PageConfig.sitespect.special;
		}

		let initParams = Helper.cloneObject(params);

		if (Helper.isObject(additionalParams)) {
			initParams = Helper.mergeObjects(params, additionalParams);
		}
		AdServiceHelper.listenEvent('initialize.end', () => {
			window.AdService.setParam(initParams);
      window.AdService.triggerOnce('begin.config');
		});
	}

	/**
	 * Calls AdService2 to make ad for given slot.
	 * @static
	 * @param {string} slot - The slot name to make ad for.
	 * @param setIframeCss {boolean} - If the iFrame CSS should be applied.
	 */
	static makeAd (slot, setIframeCss) {
		if (setIframeCss) {
			this.setIframeCss(slot);
		}
		window.AdService.push(slot);
	}

	/**
	 * Logs the current AdService2 version.
	 * @static
	 */
	static logVersion () {
		AdServiceHelper.listenEvent('initialize.end', () => {
			const versionObj = window.AdService.version;
			console.log('[AdService] Version: ' + versionObj.major + '.' + versionObj.minor + '.' + versionObj.revision);
		});
	}

	/**
	 * Render function that overrides window.AdService.render.
	 * Creates Native Teasers and Partner Links.
	 * @static
	 * @param renderObject {object} - The object containing attributes to create a rendered ad.
	 */
	static newRender (renderObject) {
		if (renderObject._type === 'teaser-hs-image' || renderObject._type === 'content-slider-news') {
			// on AdService.refresh the tagid will be appended by _x but the container name stays so we have to remove the _x
			const slotId = renderObject._meta.tagid
				.replace('_x', '')
				.replace('_united', '')
				.replace('_info', '');
			const boxContainer = document.querySelector('[data-service-slot="' + slotId + '"]');

			let caption = null;
			let label = null;

			if (renderObject._type === 'teaser-hs-image') {
				label = {
					color: LABEL_COLORS.greyLight,
					highlight: 'Anzeige',
					text: renderObject.partner
				};
			} else if (renderObject.partner || renderObject.highlight) {
				label = {
					color: LABEL_COLORS.default,
					highlight: renderObject.highlight,
					text: renderObject.partner
				};
			}

			if (renderObject.caption) {
				caption = {
					text: renderObject.caption.text,
					color: renderObject.caption.color || CAPTION_COLORS.accent,
					position: renderObject.caption.position || CAPTION_POSITION.top
				};
			}

			const TeaserClass = Vue.extend(Teaser);
			const teaserInstance = new TeaserClass({
				propsData: {
					link: {
						href: renderObject.clickurl,
						target: renderObject.target
					},
					figure: {
						img: {
							src: renderObject.imageurl,
							alt: renderObject.headline?.replace(/(##br##)/g, '\n'),
							width: 0,
							height: 0
						},
						caption
					},
					article: {
						header: {
							label,
							headline: renderObject.headline?.replace(/(##br##)/g, '\n')
						},
						summary: renderObject.subline?.replace(/(##br##)/g, '\n'),
						content: renderObject.subline?.replace(/(##br##)/g, '\n')
					}
				}
			});
			teaserInstance.$mount();

			// Add OBA-Icon if it exists
			if (renderObject._oba_direct) {
				const oba = document.createElement('a');
				const obaIcon = document.createElement('img');
				const obaText = document.createElement('span');

				oba.target = '_blank';
				oba.href = renderObject._oba_direct;
				oba.style.position = 'absolute';
				oba.style.top = '0';
				oba.style.right = '0';

				obaIcon.src = 'https://img.ui-portal.de/homepage/img/oba_icon.png';
				obaIcon.style.backgroundColor = '#ccc';
				obaIcon.style.float = 'right';

				obaText.innerText = 'Datenschutzinfo';
				obaText.style.backgroundColor = '#ccc';
				obaText.style.fontSize = '10px';
				obaText.style.lineHeight = '15px';
				obaText.style.float = 'right';
				obaText.style.paddingLeft = '5px';
				obaText.style.display = 'none';

				oba.onmouseover = () => {
					obaText.style.display = 'block';
				};

				oba.onmouseleave = () => {
					obaText.style.display = 'none';
				};

				oba.append(obaIcon);
				oba.append(obaText);
				teaserInstance.$el.append(oba);
			}

			boxContainer.prepend(teaserInstance.$el);

			const cropImage = (img) => {
				img.parentElement.style.height = '';
				const height = Math.ceil((img.clientWidth / 16) * 9 + 1);
				if (height > 1) {
					img.parentElement.style.height = height + 'px';
				}
			};

			const img = teaserInstance.$el.querySelector('img');

			if (img?.dataset?.src) {
				img.src = img.dataset.src;
			}

			img.onload = () => {
				cropImage(img);
			};

			document.addEventListener('colCountChanged', () => {
				cropImage(img);
			});

			const articleSummary = teaserInstance.$el.querySelector('p');

			if (articleSummary) {
				articleSummary.style.whiteSpace = 'pre-line';
			}

			if (renderObject.count) {
				for (let i = 0; i < renderObject.count.length; i++) {
					new Image().src = renderObject.count[i];
				}
			}

			window.AdService.trigger('ad.finished', slotId, renderObject);
		} else if (renderObject._type === 'partner-link') {
			const a = document.createElement('a');
			const img = document.createElement('img');

			a.href = renderObject.clickurl;
			a.classList.add('partner-link');
			a.target = '_blank';
			a.rel = 'sponsored';

			img.src = renderObject.imageurl;

			a.appendChild(img);

			if (renderObject.count) {
				new Image().src = renderObject.count;
			}

			document.querySelector('.partner-links').appendChild(a);
		} else {
			window.AdService._render(renderObject);
		}
	}

	/**
	 * Commits HTML to AdService2 which queues it to put it into an iFrame.
	 * @static
	 * @param slot {string} - The Slot to queue the HTML for.
	 * @param htmlSnippet {string} - The HTML snippet as string.
	 * @param refresh {boolean} - If the slot should be refreshed. Should be true if there's already an ad in there.
	 * @param actualPartType {string} - Sometimes it's necessary to insert the actual AdType in here
	 * TODO Refactor: actualPartType => actualAdType
	 */
	static queHtml (slot, htmlSnippet, refresh, actualPartType) {
		AdServiceHelper.listenEvent('initialize.end', () => {
			this.setIframeCss(slot);
			window.AdService.AdSlot(slot).queueAdHtml(htmlSnippet, actualPartType);
			window.AdService.AdSlot(slot).setFixedSize(false);

			if (refresh) {
				window.AdService.refresh(slot);
			} else {
				window.AdService.ad(slot);
			}
		});
	}

	/**
	 * Returns a XML Tag <uim:ad> with information for AdService2.
	 * @static
	 * @param part {string} - The slot to return the Tag for.
	 * @param fallbackWidth {number} - The fallback width for the Ad.
	 * @param fallbackHeight {number} - The fallback height for the Ad.
	 * @returns {string} - The Tag as a string.
	 * TODO Refactor: part => slot
	 */
	static getUIMTag (part, fallbackWidth, fallbackHeight) {
		return window.initialData.serviceResponse.response.uimAd || this.createUimTag(part, fallbackWidth, fallbackHeight);
	}

	/**
	 * Creates a XML Tag <uim:ad> with the size attribute filled.
	 * @static
	 * @param part {string} - The slot to create the Tag for.
	 * @param fallbackWidth - The fallback width that should be inserted.
	 * @param fallbackHeight - The fallback height that should be inserted.
	 * @returns {string} - The Tag as a string.
	 * TODO Refactor: part => slot
	 */
	static createUimTag (part, fallbackWidth, fallbackHeight) {
		const response = this.serviceResponse.response;

		let useWidth;
		let useHeight;

		const billboard = {
			width: 970,
			height: 250
		};
		const sky = {
			width: 160,
			height: 600
		};
		const sitebar = {
			width: 220,
			height: 600
		};

		if (this.fallback) {
			useWidth = fallbackWidth;
			useHeight = fallbackHeight;
		} else {
			switch (response.type) {
				case 'cinematic':
				case 'billboard':
					useWidth = billboard.width;
					useHeight = billboard.height;
					break;
				case 'sitebar':
				case 'double-sitebar':
					useWidth = sitebar.width;
					useHeight = sitebar.height;
					break;
				case 'bridge':
					if (part === 'middle') {
						useWidth = billboard.width;
						useHeight = billboard.height;
						break;
					}
					if (part === 'left' || part === 'right') {
						if ((response.skyVariant !== undefined && response.skyVariant.length === 0) || response.skyVariant === 'dynamic') {
							useWidth = sitebar.width;
							useHeight = sitebar.height;
							break;
						} else {
							useWidth = sky.width;
							useHeight = sky.height;
							break;
						}
					}
					break;
				case 'flagship':
					if (part === 'middle') {
						useWidth = billboard.width;
						useHeight = billboard.height;
						break;
					}
					if (part === 'right') {
						useWidth = sitebar.width;
						useHeight = sitebar.height;
						break;
					}
					break;
				default:
			}
		}

		return `<uim:ad adsize="${useWidth}x${useHeight}"></uim:ad>`;
	}

	/**
	 * Computes fallback condition given in initial ad call to see if a fallback should be shown.
	 * @static
	 * @returns {boolean} - State if fallback should be shown.
	 */
	static showFallback () {
		const fallbackConditions = window.initialData.serviceResponse.response.showFallback;
		if (!fallbackConditions) {
			this.fallback = false;
			return false;
		}

		let consentFallback;

		if (Helper.getCookieValue('euconsent-v2')) {
			consentFallback = !!fallbackConditions?.tcf;
			try {
				const consents = [];

				Object.entries(fallbackConditions.tcf).forEach(([key, value]) => {
					window.__tcfapi('getPermission', 2, (valid) => consents.push(!valid), key, value);
				});

				consentFallback = consents.some((consent) => consent);
			} catch (e) {
				// any error, mostly some data isn't set or tcfapi is not available
			}
		} else {
			consentFallback = !window.PageConfig.optin;
		}

		const agent = window.initialData.serviceResponse.agent;

		const browserFallback = agent?.browserVersion <= fallbackConditions?.browser?.[agent?.browser]; // Test if browser is even in fallback liste and if so, check if the version is smaller or equal to the fallback condition
		const osFallback = fallbackConditions?.os?.[agent?.os] === true; // Test if the os is true for full os fallback
		const osVersionFallback = agent?.osVersion <= fallbackConditions?.os?.[agent?.os]; // Test if the os version is lower or equal to the fallback condition
		const cleanFallback = !!(!window.initialData.availableResponse && fallbackConditions?.clean);

		const showFallback = !!(browserFallback || osFallback || osVersionFallback || cleanFallback || consentFallback); /* || true */ // If you want to always have a fallback uncomment the true
		this.fallback = showFallback;

		return showFallback;
	}

	/**
	 * Calls AdService2 function to check if AdBlock is detected.
	 * @static
	 * @param callBack {callback} - Called after AdBlock Detection is done with the information parameter if AdBlock was detected.
	 * @fires Document#adbDone
	 * @fires Document#bdDone
	 */
	static checkAdBlock (callBack) {
		AdServiceHelper.listenEvent('initialize.end', () => {
			const isBlocked = !!window.AdService.info?.abd()?.details?.type?.css;
			document.querySelector('html').setAttribute('data-is-blocked', isBlocked);

			if (isBlocked) {
				if (window.AdService.info?.abd()?.details?.fyi?.unitedBlocked) {
					window.AdService.setDefaultAdServer('adition-asp-info');
				} else {
					window.AdService.setDefaultAdServer('adition-asp-united-infos');
				}
			}

			const abdDone = new CustomEvent('abdDone', {
				bubbles: true,
				cancelable: true,
				detail: { abd: isBlocked, source: 'adServiceInterface.js' }
			});
			document.querySelector('body').dispatchEvent(abdDone);

			if (isBlocked) {
				const bdDone = new CustomEvent('bdDone', {
					bubbles: true,
					cancelable: true,
					detail: { abd: true, adBlockType: AD_BLOCK_TYPES.adBlocker, source: 'adServiceInterface.js' }
				});
				document.querySelector('body').dispatchEvent(bdDone);
			}

			callBack(isBlocked);
		});
	}

	/**
	 * Creates a <script>-Tag with src attribute pointing towards an 'available' ad slot.
	 * If the loading of this src fails there is a tracking blocker active.
	 * @static
	 * @param callBack {callback} - Called after Tracking Blocker Detection is done with the information parameter if Tracking Blocker was detected.
	 * @fires Document#pmdDone
	 * @fires Document#bdDone
	 * TODO Refactor and Discuss: Private mode is not correct. It should be Tracking Blocker. Please teach everyone that wording and refactor functions appropriately.
	 */
	static checkPrivateMode (callBack) {
		const pmdDoneOptions = {
			bubbles: true,
			cancelable: true,
			detail: { abd: false, source: 'adServiceInterface.js' }
		};
		const url = window.AdService.getUrlForSlot('available');

		const scriptFailed = () => {
			window.AdService.setDefaultAdServer('adition-asp-united-uimserv');
			window.AdService.setParam({ deviceclient: 'browser_clean' });

			window.initialData.availableResponse = false;
			pmdDoneOptions.detail.adBlockType = AD_BLOCK_TYPES.privateMode;
			const pmdDone = new CustomEvent('pmdDone', pmdDoneOptions);
			document.querySelector('body').dispatchEvent(pmdDone);

			const bdDone = new CustomEvent('bdDone', pmdDoneOptions);
			document.querySelector('body').dispatchEvent(bdDone);
			callBack(true);
		};

		const scriptTag = document.createElement('script');
		scriptTag.onerror = () => {
			scriptFailed();
		};
		scriptTag.onload = () => {
			window.initialData.availableResponse = true;

			pmdDoneOptions.detail.adBlockType = AD_BLOCK_TYPES.none;
			const abdDone = new CustomEvent('pmdDone', pmdDoneOptions);
			document.querySelector('body').dispatchEvent(abdDone);

			const bdDone = new CustomEvent('bdDone', pmdDoneOptions);
			document.querySelector('body').dispatchEvent(bdDone);

			callBack(false);
		};

		scriptTag.src = url;

		const appendToMe = document.querySelector('[data-available]');
		if (appendToMe) {
			appendToMe.appendChild(scriptTag);
		}

		if (!url || !appendToMe) {
			scriptFailed();
		}
	}

	/**
	 * Loop to create the three partner links in Navigation Bar.
	 * @static
	 * Calls {@link makeAd}.
	 */
	static makePartnerlinks () {
		for (let i = 1; i <= 3; i++) {
			document.querySelector(`[data-service-slot=promo_${i}]`) && this.makeAd('promo_' + i);
		}
	}

	/**
	 * Loop to create the five Vorteilswelt Ads in content.
	 * @static
	 * Calls {@link makeAd}.
	 */
	static makeVorteilswelt () {
		if (window.PageConfig.vorteilswelt) {
			for (let i = 5; i >= 1; i--) {
				document.querySelector(`[data-service-slot=slider_1_${i}]`) && this.makeAd('slider_1_' + i);
			}
		}
	}

	/**
	 * Increments the Ad Count in AdService2.
	 * Initial AdCall is not counted correctly because it's in the backend so this is necessary before first AdCall in Frontend.
	 * @static
	 */
	static incrementAc () {
		AdServiceHelper.listenEvent('initialize.end', () => {
			window.AdService.getDefaultAdServer().increaseRequestCount();
		});
	}
}

export { AdServiceInterface };
